import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { isValidPhoneNumber } from 'react-phone-number-input';
import { variants } from '@aldebaran/common/qVariants';

import { FieldHelper, FieldLabel } from '@/components/PatientData/FieldHelper';
import { PatientStepFooter } from '@/components/PatientData/PatientStepFooter';
import { PatientPageLayout } from '@/components/PatientPageLayout';
import { ResponsePhoneField } from '@/components/ResponsePhoneField';
import { ResponseTextField } from '@/components/ResponseTextField';
import { usePatientDataCollectionConfig } from '@/hooks/usePatientDataCollectionConfig';
import { useSavePatientStep } from '@/hooks/useSavePatientStep';
import { isValidEmail } from '@/util/email';
import { RootState } from '@/types/graph';
import { scrollToTop } from '@/util/scroll';

const REQUIRED_FIELD_MESSAGE = 'Ce champ est obligatoire';

type ContactErrors = {
  phone?: string;
  email?: string;
};

// The form initialises from the stored patient once, so remount it when the
// patient changes rather than re-syncing its state.
export const PatientContact = () => {
  const patientId = useSelector(
    (state: RootState) => state.consultation.patient?.id,
  );
  return <PatientContactForm key={patientId} />;
};

const PatientContactForm = () => {
  const { patient, save, isSaving, submitError, clearSubmitError } =
    useSavePatientStep('PATIENT_CONTACT');
  const isSelf = useSelector(
    (state: RootState) => state.questionnaire.interlocuter !== 'OTHER',
  );
  // A kiosk check-in continued on the phone texts the patient a link once
  // they are registered (ALDO-2759), so say so where they give their number
  const continuesBySms = useSelector(
    (state: RootState) =>
      !!state.consultation.practice?.kioskContinuationSmsEnabled &&
      !!state.graph.variant &&
      !!variants[state.graph.variant]?.continuationVariant,
  );
  const config = usePatientDataCollectionConfig();

  const showPhone = config.PHONE.enabled;
  const showEmail = config.EMAIL.enabled;

  const [phone, setPhone] = useState(patient?.phone ?? '');
  const [email, setEmail] = useState(patient?.email ?? '');
  const [errors, setErrors] = useState<ContactErrors>({});

  useEffect(() => {
    scrollToTop();
  }, []);

  const validate = (): ContactErrors => {
    const nextErrors: ContactErrors = {};

    if (showPhone) {
      const trimmedPhone = phone.trim();
      if (!trimmedPhone) {
        if (config.PHONE.required) nextErrors.phone = REQUIRED_FIELD_MESSAGE;
      } else if (!isValidPhoneNumber(trimmedPhone)) {
        nextErrors.phone = 'Numéro de téléphone invalide';
      }
    }

    if (showEmail) {
      const trimmedEmail = email.trim();
      if (!trimmedEmail) {
        if (config.EMAIL.required) nextErrors.email = REQUIRED_FIELD_MESSAGE;
      } else if (!isValidEmail(trimmedEmail)) {
        nextErrors.email = 'Adresse email invalide';
      }
    }

    return nextErrors;
  };

  const handleSubmit = () => {
    const nextErrors = validate();
    setErrors(nextErrors);
    clearSubmitError();
    if (Object.keys(nextErrors).length > 0) return;

    // Only the collected fields are sent, so a field the practice does not ask
    // for is never overwritten.
    save(
      {
        ...(showPhone && { phone: phone.trim() || null }),
        ...(showEmail && { email: email.trim() || null }),
      },
      {
        onRejectedFields: (fields) => {
          const rejectedErrors: ContactErrors = {
            ...(fields.includes('phone') && {
              phone: 'Numéro de téléphone invalide',
            }),
            ...(fields.includes('email') && {
              email: 'Adresse email invalide',
            }),
          };
          setErrors(rejectedErrors);
          return Object.keys(rejectedErrors).length > 0;
        },
      },
    );
  };

  return (
    <PatientPageLayout
      columnClassName="w-full"
      title={
        <span className="flex flex-col">
          <p className="text-2xl min-[23rem]:text-3.5xl font-semibold nxs:text-center">
            {isSelf ? 'Vos coordonnées' : 'Les coordonnées du patient'}
          </p>
          <p className="nxs:text-center font-light text-sm mt-2">
            {showPhone && continuesBySms
              ? 'Vous recevrez un SMS pour compléter un questionnaire médical pendant votre attente'
              : isSelf
              ? 'Pour que le cabinet puisse vous joindre'
              : 'Pour que le cabinet puisse joindre le patient'}
          </p>
        </span>
      }
      footer={
        <PatientStepFooter
          onSubmit={handleSubmit}
          isSaving={isSaving}
          submitError={submitError}
          disabled={!!errors.phone || !!errors.email}
        />
      }
    >
      <div className="flex flex-col w-full items-center gap-3 py-4 z-10">
        <div className="flex flex-col w-full gap-4 max-w-86">
          {showPhone && (
            <div className="flex flex-col gap-1">
              <FieldLabel optional={!config.PHONE.required}>
                Téléphone
              </FieldLabel>
              <ResponsePhoneField
                label="06 12 34 56 78"
                value={phone}
                onChange={(value) => {
                  setPhone(value || '');
                  setErrors((current) => ({ ...current, phone: undefined }));
                }}
                error={!!errors.phone}
              />
              <FieldHelper error>{errors.phone}</FieldHelper>
            </div>
          )}

          {showEmail && (
            <div className="flex flex-col gap-1">
              <FieldLabel optional={!config.EMAIL.required}>Email</FieldLabel>
              <ResponseTextField
                label="nom@exemple.fr"
                value={email}
                onChange={(value) => {
                  setEmail(value || '');
                  setErrors((current) => ({ ...current, email: undefined }));
                }}
                error={!!errors.email}
              />
              <FieldHelper error>{errors.email}</FieldHelper>
            </div>
          )}
        </div>
      </div>
    </PatientPageLayout>
  );
};
